import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faAngleLeft, faAngleRight } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';
import { useSelector } from 'react-redux';

const AmenitiesTypeForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { user } = useSelector((state) => state.auth);

    const isEdit = !!id;
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [formData, setFormData] = useState({
        amenityTypeName: '',
        image: '',
        isActiveId: 1
    });

    useEffect(() => {
        if (isEdit) {
            const fetchAmenityType = async () => {
                setIsFetching(true);
                try {
                    const response = await apiServices({
                        method: "GET",
                        url: `/ResidentialAmenity/GetAmenityTypeByTypeId/${id}`
                    });
                    if (response.success || response.isSuccess) {
                        const data = response.data || response.data?.[0];
                        if (data) {
                            setFormData({
                                amenityTypeName: data.amenityTypeName || '',
                                image: data.image || '',
                                isActiveId: data.isActiveId !== undefined ? data.isActiveId : 1
                            });
                        }
                    }
                } catch (error) {
                    console.error("Error fetching amenity type:", error);
                    toast.error("Failed to load amenity type.");
                } finally {
                    setIsFetching(false);
                }
            };

            // If data is passed via router state, use it, otherwise fetch
            if (location.state && location.state.amenityData) {
                const data = location.state.amenityData;
                setFormData({
                    amenityTypeName: data.amenityTypeName || '',
                    image: data.image || '',
                    isActiveId: data.isActiveId !== undefined ? data.isActiveId : 1
                });
            } else {
                fetchAmenityType();
            }
        }
    }, [isEdit, id, location.state]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                setFormData(prev => ({ ...prev, image: reader.result }));
            };
            reader.readAsDataURL(file);
        }
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => ({ ...prev, [key]: option ? option.value : null }));
    };

    const validateForm = () => {
        if (!formData.amenityTypeName.trim()) return toast.error("Amenity name is required"), false;
        if (!formData.image) return toast.error("Please upload an image"), false;
        if (formData.isActiveId === null || formData.isActiveId === undefined) return toast.error("Please select status"), false;
        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        setIsLoading(true);
        try {
            const body = {
                ...formData,
                createdBy: user?.name || '',
                siteId: user?.siteId || null
            };

            const config = {
                method: isEdit ? "PUT" : "POST",
                url: isEdit 
                    ? `/ResidentialAmenity/UpdateResidentialAmenityType/${id}` 
                    : `/ResidentialAmenity/PostResidentialAmenityType`,
                data: body
            };

            const response = await apiServices(config);

            if (response.isSuccess || response.success || response.IsSuccess) {
                toast.success(`Amenity Type ${isEdit ? 'updated' : 'added'} successfully!`);
                navigate('/zentres/Master-management/AmenitiesType');
            } else {
                toast.error(`Failed to ${isEdit ? 'update' : 'add'} amenity type.`);
            }
        } catch (error) {
            console.error("Submit error:", error);
            toast.error("Something went wrong while saving the amenity type.");
        } finally {
            setIsLoading(false);
        }
    };

    const statusOptions = [
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 0 }
    ];

    if (isFetching) {
        return (
            <div className="flex justify-center items-center h-screen bg-white rounded-lg shadow-sm border border-gray-100">
                <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-gray-300" style={{ color: primaryColor() }} />
            </div>
        );
    }

    return (
        <div className="bg-white px-6 py-4 border flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Amenity Type`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} amenity type.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                <div className="md:col-span-2 lg:col-span-1">
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="amenityTypeName">
                        Amenity Type Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none focus:border-indigo-500 transition-colors bg-gray-50/50"
                        type="text"
                        id="amenityTypeName"
                        value={formData.amenityTypeName}
                        onChange={handleInputChange}
                        placeholder="Enter amenity name"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={statusOptions}
                    value={statusOptions.find(o => o.value === formData.isActiveId) || null}
                    onChange={(opt) => handleSelectChange('isActiveId', opt)}
                />

                <div className="md:col-span-2">
                    <label className="block text-xs font-bold text-gray-700 mb-1" htmlFor="image">
                        Image <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="file"
                        id="image"
                        accept="image/*"
                        onChange={handleFileChange}
                        className="w-full px-3 py-2 border rounded-md text-sm outline-none bg-gray-50/50"
                    />
                    {formData.image && (
                        <div className="mt-4 border rounded-md p-2 w-32 h-32 flex items-center justify-center bg-gray-50">
                            <img src={formData.image} alt="Preview" className="max-w-full max-h-full object-contain rounded" />
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate('/zentres/Master-management/AmenitiesType')}
                    className="px-3 py-2 border rounded-md text-xs flex items-center gap-2 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        if (!isLoading) {
                            e.currentTarget.style.backgroundColor = primaryColor();
                            e.currentTarget.style.color = '#FFFFFF';
                        }
                    }}
                    onMouseLeave={(e) => {
                        if (!isLoading) {
                            e.currentTarget.style.backgroundColor = secondaryColor();
                            e.currentTarget.style.color = primaryColor();
                        }
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default AmenitiesTypeForm;
